import {RefObject, useCallback, useEffect, useState} from "react";
import {PartnerCompanyStatus, PartnerCompanyType, PartnerCompanyVO} from "../../../../types/partnerCompany";
import {usePartnerCompaniesGetter} from "../../../sub/partner/usePartnerCompaniesGetter";

const BASE_ANIMATION_SPEED = 60;
const DEFAULT_PAGE_SIZE = 1000;
interface Props{
    domesticContainerRef: RefObject<HTMLDivElement>;
    domesticWrapperRef: RefObject<HTMLDivElement>;
    overseasContainerRef: RefObject<HTMLDivElement>;
    overseasWrapperRef: RefObject<HTMLDivElement>;
}

/**
 * 메인 화면에서 보이는 협력사 목록
 */
export function usePartnershipFunctions(
    {domesticContainerRef, domesticWrapperRef, overseasContainerRef, overseasWrapperRef}:Props
) {
    const { getPartnerCompanies } = usePartnerCompaniesGetter();
    const [domesticPartnerCompanies, setDomesticPartnerCompanies] = useState<PartnerCompanyVO[] | null>(null);
    const [overseasPartnerCompanies, setOverseasPartnerCompanies] = useState<PartnerCompanyVO[] | null>(null);

    const [isDomesticOverflowing, setIsDomesticOverflowing] = useState<boolean>(false);
    const [isOverseasOverflowing, setIsOverseasOverflowing] = useState<boolean>(false);

    const [domesticAnimationDuration, setDomesticAnimationDuration] = useState(60);
    const [overseasAnimationDuration, setOverseasAnimationDuration] = useState(60);

    const sortNumericOrderDesc = useCallback(
        (companies: PartnerCompanyVO[]) => companies.sort((a, b) => b.numericOrder - a.numericOrder),
        []
    );

    // 국내 협력사 / 해외 협력사 분리
    useEffect(() => {
        const status:PartnerCompanyStatus[] = [PartnerCompanyStatus.EXPOSED]

        getPartnerCompanies({
            status: status,
            pageCondition: {
                size: DEFAULT_PAGE_SIZE,
            },
        })
            .then((companies: PartnerCompanyVO[]) => {
                const voList = companies.filter((vo: PartnerCompanyVO) => vo.type === PartnerCompanyType.DOMESTIC);
                setDomesticPartnerCompanies(sortNumericOrderDesc(voList));
                setOverseasPartnerCompanies(
                    sortNumericOrderDesc(companies.filter((vo) => vo.type === PartnerCompanyType.OVERSEAS))
                );
            })
            .catch(() => {
                alert("일시적인 문제가 발생했습니다.\n다시 시도해주세요.")
            });
    }, [getPartnerCompanies, sortNumericOrderDesc]);


    // 이미지 총 길이 계산
    const calculateOverflow = useCallback(() => {
        // 국내 협력사 오버플로우 계산
        if (domesticContainerRef.current && domesticWrapperRef.current && domesticPartnerCompanies) {
            const containerWidth = domesticContainerRef.current.offsetWidth;
            const contentWidth = domesticWrapperRef.current.scrollWidth;
            setIsDomesticOverflowing(contentWidth > containerWidth);

            const newDuration = contentWidth / BASE_ANIMATION_SPEED;
            setDomesticAnimationDuration(newDuration);
        }

        // 해외 협력사 오버플로우 계산
        if (overseasContainerRef.current && overseasWrapperRef.current && overseasPartnerCompanies) {
            const containerWidth = overseasContainerRef.current.offsetWidth;
            const contentWidth = overseasWrapperRef.current.scrollWidth;
            setIsOverseasOverflowing(contentWidth > containerWidth);

            const newDuration = contentWidth / BASE_ANIMATION_SPEED;
            setOverseasAnimationDuration(newDuration);
        }
        // eslint-disable-next-line
    }, [domesticPartnerCompanies, overseasPartnerCompanies]);


    // 이미지가 모드 로드되고 리사이즈 하는 함수
    const waitForImagesAndCalculate = () => {
        let domesticImages: HTMLImageElement[] = [];
        let overseasImages: HTMLImageElement[] = [];

        if (domesticWrapperRef.current) {
            domesticImages = Array.from(domesticWrapperRef.current.querySelectorAll('img'));
        }

        if (overseasWrapperRef.current) {
            overseasImages = Array.from(overseasWrapperRef.current.querySelectorAll('img'));
        }

        const allImages = [...domesticImages, ...overseasImages];

        // 이미지가 없으면 바로 계산
        if (allImages.length === 0) {
            calculateOverflow();
            return;
        }

        // 이미지 로드 완료 여부 확인
        let loadedImagesCount = 0;

        // 각 이미지에 로드 이벤트 리스너 추가
        allImages.forEach(img => {
            if (img.complete) {
                loadedImagesCount++;
            } else {
                img.addEventListener('load', () => {
                    loadedImagesCount++;
                    if (loadedImagesCount === allImages.length) {
                        calculateOverflow();
                    }
                }, { once: true });

                // 이미지 로드 실패 시에도 카운트
                img.addEventListener('error', () => {
                    loadedImagesCount++;
                    if (loadedImagesCount === allImages.length) {
                        calculateOverflow();
                    }
                }, { once: true });
            }
        });

        // 이미 모든 이미지가 로드되었다면 바로 계산
        if (loadedImagesCount === allImages.length) {
            calculateOverflow();
        }
    };

    return {
        calculateOverflow,
        waitForImagesAndCalculate,
        isDomesticOverflowing, isOverseasOverflowing,
        domesticAnimationDuration, overseasAnimationDuration,
        domesticPartnerCompanies, overseasPartnerCompanies
    };
}
